import React, { useEffect, useState } from 'react';
import { deliveryAPI, BinsPricing, RideAlong } from '../../../services/deliveryAPI';
import RideAlongDialog from '../../../components/BridgeClub/RideAlongDialog';
import { shouldShowFormType } from '../../../components/BridgeClub/feed/feedConstants';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import FeedProductPicker, { FeedSelection, emptyFeedSelection } from '../../../components/BridgeClub/feed/FeedProductPicker';
import { hasFeedChoice, resolveFeedProduct } from '../../../components/BridgeClub/feed/resolveFeedProduct';
import { parseApiError } from '../../../utils/apiErrors';

const RED = '#B91C1C';

// Route days are fixed per town but not decided yet — PROVISIONAL Mon–Fri until Brad sees
// where the leads cluster (the lead map). This list mirrors DeliverySubscriber.PREFERRED_DAY_CHOICES.
const WEEKDAYS = [
  { v: 'tuesday', l: 'Tuesday' },
  { v: 'wednesday', l: 'Wednesday' },
  { v: 'thursday', l: 'Thursday' },
  { v: 'friday', l: 'Friday' },
];

interface Props {
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void; // "skip delivery setup for now" — lands on the dashboard
}

/**
 * Step 3 — Delivery preferences. Two questions, no cadence (deliveries are bin-driven):
 *   a) which bins should we keep filled? (at least one; non-Red-Bridge bins are shown but
 *      not selectable — the member keeps buying those themselves)
 *   b) which weekday suits them? (a preference, not a frequency)
 * Prices are the Treasure Valley bag price + 6% Idaho tax, computed server-side.
 */
const DeliveryPreferencesStep: React.FC<Props> = ({ onBack, onNext, onSkip }) => {
  const [pricing, setPricing] = useState<BinsPricing | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [day, setDay] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Inline "set the feed" — which bin is being edited, and the picker's selection.
  const [editingBin, setEditingBin] = useState<number | null>(null);
  const [pickerSel, setPickerSel] = useState<FeedSelection>(emptyFeedSelection);
  const [savingProduct, setSavingProduct] = useState(false);
  // The bag needs setting up before we can deliver it. Not an error - the feed
  // was recorded - so it must not be shown in red as a failure.
  const [notice, setNotice] = useState('');
  // The soft no for a pet-food-only customer (see petOnly below): what the server said
  // when they answered yes, and the button's busy state.
  const [petOnlyNoted, setPetOnlyNoted] = useState<string | null>(null);
  const [notingPetOnly, setNotingPetOnly] = useState(false);
  // "ALSO BRING" IN SETUP (Brad, 2026-09-22): a customer signing up for wild bird seed
  // and dog food should not have to finish setup and then find the Delivery page. The
  // same dialog and endpoints as the Delivery page; optional, no step of its own.
  const [rideAlongs, setRideAlongs] = useState<RideAlong[]>([]);
  const [rideAlong, setRideAlong] = useState<RideAlong | null | undefined>(undefined);

  const reload = async (preserveSelection = true) => {
    const p = await deliveryAPI.getBinsPricing();
    setPricing(p);
    if (!preserveSelection) {
      setSelected(new Set(p.bins.filter((b) => b.selected).map((b) => b.bin_id)));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await reload(false);
        // Not fatal: a failure here leaves the section empty, never the step broken.
        deliveryAPI.listRideAlongs().then((r) => setRideAlongs(r.ride_alongs || [])).catch(() => {});
      } catch {
        setError('We could not load your bins. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setBinFeed = async (binId: number) => {
    if (!hasFeedChoice(pickerSel)) return;
    setSavingProduct(true);
    setError('');
    setNotice('');
    try {
      // THE RECOVERY PATH HAD THE SAME HOLE AS THE BIN BUILDER. It read
      // selectedProductId only, so a customer could search the distributor
      // catalog, see their food, click it - and the Save button stayed grey,
      // because a catalog pick sets catalogProductId and nothing here looked at
      // it. Two catalogs offered, one accepted. resolveFeedProduct is now the
      // single definition of what a picker selection means.
      const { feedProductId, notice: said } = await resolveFeedProduct(pickerSel);
      if (!feedProductId) {
        setError('Please choose a feed from the list.');
        return;
      }
      // The form picked with the feed reaches the bin: it decides the bag and the price.
      const bin = pricing?.bins.find((b) => b.bin_id === binId);
      const form = bin && shouldShowFormType(bin.feed_category) ? { form_type: pickerSel.formType } : {};
      await bridgeClubAPI.updateFeedBin(binId, { feed_product_id: feedProductId, ...form });
      setEditingBin(null);
      setPickerSel(emptyFeedSelection);
      if (said) setNotice(said);
      await reload();          // the bin may now be deliverable
    } catch {
      setError('We could not set that feed. Please try again.');
    } finally {
      setSavingProduct(false);
    }
  };

  const toggle = (binId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(binId)) next.delete(binId);
      else next.add(binId);
      return next;
    });
  };

  const canContinue = selected.size > 0 && !!day && !saving;

  const save = async () => {
    if (!canContinue) return;
    setSaving(true);
    setError('');
    try {
      await deliveryAPI.setPreferences(Array.from(selected), day);
      onNext();
    } catch (e: any) {
      // String(errs) on a DRF field-error object renders "[object Object]".
      setError(parseApiError(e, {
        fallback: 'Could not save your preferences.',
      }).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', color: '#666', padding: 40 }}>Loading your bins…</div>;
  }

  const deliverable = pricing?.bins.filter((b) => b.deliverable) || [];
  const theirOwn = pricing?.bins.filter((b) => !b.deliverable) || [];
  // THE SOFT NO (Brad, 2026-09-24: "don't want to say no to them if we may find it
  // possible to fulfill their subscription"). Every delivery carries a Red Bridge bag, so
  // a customer whose bins are all pet food or other brands cannot subscribe today. They
  // are told so, and asked one question - tell you if that changes? - which is recorded
  // for a manager. A manager who allows it clears the block for this customer alone.
  const petOnly = !!pricing && pricing.bins.length > 0 && !pricing.bins.some((b) => b.milled)
    && !pricing.pet_only?.allowed;
  const notePetOnly = async () => {
    setNotingPetOnly(true);
    try {
      const r = await deliveryAPI.notePetOnlyInterest();
      setPetOnlyNoted(r.message);
    } catch {
      setError('We could not save that. Please try again.');
    } finally {
      setNotingPetOnly(false);
    }
  };

  return (
    <div style={{ maxWidth: 620, margin: '0 auto', padding: '0 16px 32px' }}>
      <h2 style={{ color: RED, marginBottom: 4 }}>Delivery preferences</h2>
      <p style={{ color: '#555', marginTop: 0 }}>
        Your bins tell us when to come, so there's no schedule to pick. Just choose which
        bins we should keep filled and the weekday that suits you.
      </p>

      {error && (
        <div style={{ background: '#fee', color: '#b91c1c', border: '1px solid #fcc', borderRadius: 8, padding: 10, margin: '8px 0' }}>
          {error}
        </div>
      )}

      <h3 style={{ color: '#333', marginBottom: 8 }}>Which feeds should we keep you stocked on?</h3>
      {deliverable.length === 0 && !petOnly && (
        <p style={{ color: '#b45309' }}>None of your bins are Red Bridge feed we deliver yet.</p>
      )}
      {petOnly && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '12px 14px', margin: '8px 0 12px' }}>
          <div style={{ fontWeight: 700, color: '#92400e' }}>We can't deliver pet food on its own yet.</div>
          <p style={{ margin: '6px 0 0', color: '#78350f', fontSize: 14 }}>
            Every delivery carries at least one bag of Red Bridge feed, and pet food rides along with it.
            Add a bin of Red Bridge feed and we can start. Or, if that changes for your area, we'd like to tell you.
          </p>
          {(petOnlyNoted || pricing?.pet_only?.interest_at) ? (
            <p style={{ margin: '8px 0 0', color: '#166534', fontWeight: 600, fontSize: 14 }}>
              {petOnlyNoted || "Noted. We'll tell you if we can bring pet food on its own to your area."}
            </p>
          ) : (
            <button
              type="button"
              onClick={notePetOnly}
              disabled={notingPetOnly}
              style={{ marginTop: 10, minHeight: 40, padding: '0 16px', border: 'none', borderRadius: 8, background: RED, color: '#fff', fontWeight: 700, cursor: 'pointer' }}
            >
              {notingPetOnly ? 'Saving…' : 'Yes, tell me if that changes'}
            </button>
          )}
        </div>
      )}
      {deliverable.map((b) => (
        <label
          key={b.bin_id}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8, cursor: 'pointer', background: selected.has(b.bin_id) ? '#fef2f2' : '#fff' }}
        >
          <input type="checkbox" checked={selected.has(b.bin_id)} onChange={() => toggle(b.bin_id)} style={{ width: 20, height: 20 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#111' }}>{b.name}</div>
            <div style={{ fontSize: 13, color: '#666' }}>{b.product_name} · {b.size_lbs} lb bag</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {b.unit_price != null ? (
              <>
                <div style={{ fontWeight: 700, color: '#111' }}>${b.total}/bag</div>
                <div style={{ fontSize: 12, color: '#888' }}>${b.unit_price} + ${b.tax} tax</div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: '#aaa' }}>price unavailable</div>
            )}
          </div>
        </label>
      ))}

      {notice && (
        <div style={{
          color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d',
          borderRadius: 8, padding: '10px 12px', margin: '10px 0', fontSize: 14,
        }}>
          {notice}
        </div>
      )}

      {theirOwn.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <h4 style={{ color: '#555', margin: '12px 0 6px', fontSize: 14 }}>Not yet deliverable</h4>
          {theirOwn.map((b) => (
            <div key={b.bin_id} style={{ padding: 12, border: '1px dashed #e5e7eb', borderRadius: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#555' }}>{b.name}</div>
                  {/* THE SERVER SAYS WHY. This used to hardcode "we don't
                      recognise this bin's feed" for every case, including bags
                      we recognise perfectly well and have simply not priced for
                      Treasure Valley - which sent the customer off to re-pick a
                      product that was right the first time. The reason comes
                      from the same _bin_state branch the admin screen reads, so
                      the member and Brad are never told different stories. */}
                  <div style={{ fontSize: 13, color: '#888' }}>
                    {b.not_deliverable_reason
                      || "We don't know which feed is in this bin yet."}
                  </div>
                </div>
                {editingBin !== b.bin_id && (
                  <button onClick={() => { setEditingBin(b.bin_id); setPickerSel(emptyFeedSelection); }} style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: RED, fontWeight: 600, cursor: 'pointer' }}>
                    Set the feed
                  </button>
                )}
              </div>
              {editingBin === b.bin_id && (
                <div style={{ marginTop: 10 }}>
                  <FeedProductPicker feedCategory={b.feed_category} value={pickerSel} onChange={setPickerSel} formFilter />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                    <button onClick={() => { setEditingBin(null); setPickerSel(emptyFeedSelection); }} style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={() => setBinFeed(b.bin_id)} disabled={!hasFeedChoice(pickerSel) || savingProduct} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: (!hasFeedChoice(pickerSel) || savingProduct) ? '#9ca3af' : RED, color: '#fff', fontWeight: 700, cursor: (!hasFeedChoice(pickerSel) || savingProduct) ? 'default' : 'pointer' }}>
                      {savingProduct ? 'Saving…' : 'Save feed'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <h3 style={{ color: '#333', margin: '20px 0 8px' }}>Anything else to bring along?</h3>
      <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
        Wild bird seed, litter, treats &ndash; anything from our catalog that doesn't live in a bin.
        It comes with your feed delivery. Optional.
      </p>
      {rideAlongs.map((r) => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{r.bag_count} × {r.product.full_name || `${r.product.brand} ${r.product.display_name}`}</div>
            <div style={{ fontSize: 13, color: '#666' }}>{r.cadence_label}{r.price ? ` · $${Number(r.price).toFixed(2)} each` : ''}</div>
          </div>
          <button type="button" onClick={() => setRideAlong(r)} style={{ minHeight: 40, padding: '0 12px', border: `1px solid ${RED}`, borderRadius: 8, background: '#fff', color: RED, fontWeight: 600, cursor: 'pointer' }}>Change</button>
          <button type="button" style={{ minHeight: 40, padding: '0 6px', border: 'none', background: 'none', color: '#6b7280', fontWeight: 600, cursor: 'pointer' }}
            onClick={() => { if (window.confirm(`Stop bringing ${r.product.full_name || r.product.display_name}?`)) deliveryAPI.stopRideAlong(r.id).then((page) => setRideAlongs(page.ride_alongs || [])).catch(() => setError('We could not stop that. Please try again.')); }}>
            Stop
          </button>
        </div>
      ))}
      <button type="button" onClick={() => setRideAlong(null)} style={{ width: '100%', minHeight: 44, marginTop: 6, border: `1px solid ${RED}`, borderRadius: 8, background: '#fff', color: RED, fontWeight: 600, cursor: 'pointer' }}>
        + Also bring something
      </button>
      {rideAlong !== undefined && (
        <RideAlongDialog
          existing={rideAlong}
          onClose={() => setRideAlong(undefined)}
          onSaved={(page) => { setRideAlongs(page.ride_alongs || []); setRideAlong(undefined); }}
        />
      )}

      <h3 style={{ color: '#333', margin: '20px 0 8px' }}>Which day suits you?</h3>
      <select value={day} onChange={(e) => setDay(e.target.value)} style={{ width: '100%', padding: 12, fontSize: 16, borderRadius: 10, border: '1px solid #d1d5db' }}>
        <option value="">Choose a day…</option>
        {WEEKDAYS.map((d) => (
          <option key={d.v} value={d.v}>{d.l}</option>
        ))}
      </select>
      <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
        Route days are still being set — we'll confirm your day before your first delivery.
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <button onClick={onBack} style={{ padding: '10px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer' }}>
          ← Back
        </button>
        <button onClick={save} disabled={!canContinue} style={{ padding: '10px 20px', border: 'none', borderRadius: 8, background: canContinue ? RED : '#9ca3af', color: '#fff', fontWeight: 700, cursor: canContinue ? 'pointer' : 'default' }}>
          {saving ? 'Saving…' : 'Next: Property →'}
        </button>
      </div>
      {selected.size === 0 && (
        <p style={{ textAlign: 'right', color: '#b45309', fontSize: 13, marginTop: 6 }}>
          Choose at least one to continue.
        </p>
      )}

      {/* Always an exit: a customer with only third-party bins must be able to leave. */}
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <button onClick={onSkip} style={{ background: 'none', border: 'none', color: '#6b7280', textDecoration: 'underline', cursor: 'pointer', fontSize: 14 }}>
          Skip delivery setup for now
        </button>
      </div>
    </div>
  );
};

export default DeliveryPreferencesStep;
